import React, { useState, useEffect } from "react";
import SwModal from "../SwModal/SwModal";
import SpInput from "../SpInput/SpInput";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";
import { Trash2, Plus, CreditCard } from "lucide-react";
import { selectEventVendorLoading  } from "../../redux/selectors/eventVendorSelectors";

const SwEventModal = ({ onClose, open, mode = "add", eventData = null }) => {
  const dispatch = useDispatch();
  
  // Replace with your actual loading selectors
  const isLoading = useSelector(selectEventVendorLoading);

  const isEditMode = mode === "edit";

  const formik = useFormik({
    initialValues: {
      name: '',
      ticketTypes: [
        { accessType: 'Regular', price: '' },
        { accessType: 'VIP', price: '' },
        { accessType: 'Table', price: '' }
      ],
      accessSlots: {
        Regular: '',
        VIP: '',
        Table: ''
      },
      paymentOptions: {
        bankTransfer: false,
        card: false
      }
    },
    onSubmit: async (values) => {
      try {
        const eventPayload = {
          name: values.name,
          ticketTypes: values.ticketTypes.filter(ticket => ticket.price),
          accessSlots: values.accessSlots,
          paymentOptions: values.paymentOptions
        };

        if (isEditMode && eventData?.id) {
          // Update existing event
          eventPayload.id = eventData.id;
          // await dispatch(updateEvent(eventPayload));
          message.success("Event updated successfully!");
        } else {
          // Create new event
          // await dispatch(createEvent(eventPayload));
          message.success("Event created successfully!");
        }
        
        handleClose();
        
        // Refresh event list if needed
        // dispatch(listEvents());
      } catch (error) {
        message.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} event`);
      }
    }
  });

  // Populate form when in edit mode
  useEffect(() => {
    if (isEditMode && eventData) {
      formik.setValues({
        name: eventData.name || '',
        ticketTypes: eventData.ticketTypes || [
          { accessType: 'Regular', price: '' },
          { accessType: 'VIP', price: '' },
          { accessType: 'Table', price: '' }
        ],
        accessSlots: eventData.accessSlots || {
          Regular: '',
          VIP: '',
          Table: ''
        },
        paymentOptions: eventData.paymentOptions || {
          bankTransfer: false,
          card: false
        }
      });
    } else {
      // Reset form for add mode
      formik.resetForm();
    }
  }, [isEditMode, eventData, open]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleSaveChanges = () => {
    formik.submitForm();
  };

  const handleChange = (key, value) => {
    formik.setFieldValue(key, value);
  };

  const handleTicketTypeChange = (index, field, value) => {
    const updatedTicketTypes = [...formik.values.ticketTypes];
    updatedTicketTypes[index][field] = value;
    formik.setFieldValue('ticketTypes', updatedTicketTypes);
  };

  const handleAccessSlotChange = (accessType, value) => {
    formik.setFieldValue(`accessSlots.${accessType}`, value);
  };

  const handlePaymentOptionChange = (option, checked) => {
    formik.setFieldValue(`paymentOptions.${option}`, checked);
  };

  const addNewTicketType = () => {
    const newTicketType = { accessType: '', price: '' };
    formik.setFieldValue('ticketTypes', [...formik.values.ticketTypes, newTicketType]);
  };

  const removeTicketType = (index) => {
    const updatedTicketTypes = formik.values.ticketTypes.filter((_, i) => i !== index);
    formik.setFieldValue('ticketTypes', updatedTicketTypes);
  };

  // Dynamic modal content based on mode
  const modalTitle = isEditMode ? "Edit Event Details" : "Add Event Details";
  const modalSubtitle = isEditMode 
    ? "Update event information below" 
    : "Enter event details below";

  return (
    <SwModal
      title={modalTitle}
      subtitle={modalSubtitle}
      onClose={handleClose}
      open={open}
      onSaveChanges={handleSaveChanges}
      saveButtonLoading={isLoading}
      saveButtonText={isEditMode ? "Update" : "Save"}
      width="600px"
    >
      <form onSubmit={formik.handleSubmit}>
        {/* Main Details Section */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '500', 
            color: '#9E9BAC', 
            marginBottom: 16 
          }}>
            Main Details
          </div>
          
          <div style={{ marginBottom: 24 }}>
            <SpInput
              htmlFor="name"
              label="Event Name"
              placeholder="Enter event name"
              value={formik.values.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </div>
        </div>

        {/* Ticket Details Section */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '500', 
            color: '#9E9BAC', 
            marginBottom: 16 
          }}>
            Ticket Details
          </div>
          
          <div style={{ display: 'flex', gap: '16px', marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: 8, display: 'block' }}>
                Access Type
              </label>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: 8, display: 'block' }}>
                Price
              </label>
            </div>
            <div style={{ width: '40px' }}></div>
          </div>

          {formik.values.ticketTypes.map((ticket, index) => (
            <div key={index} style={{ display: 'flex', gap: '16px', marginBottom: 16, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <SpInput
                  placeholder="Access type"
                  value={ticket.accessType}
                  onChange={(e) => handleTicketTypeChange(index, 'accessType', e.target.value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <SpInput
                  placeholder="Price"
                  type="number"
                  value={ticket.price}
                  onChange={(e) => handleTicketTypeChange(index, 'price', e.target.value)}
                />
              </div>
              {formik.values.ticketTypes.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeTicketType(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#EF4444',
                    padding: '8px'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addNewTicketType}
            style={{
              background: 'none',
              border: 'none',
              color: '#9747FF',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 0'
            }}
          >
            <Plus size={16} />
            Add New Access
          </button>
        </div>

        {/* Access Slots Section */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '500', 
            color: '#9E9BAC', 
            marginBottom: 16 
          }}>
            Access Slots
          </div>
          
          {Object.keys(formik.values.accessSlots).map((accessType) => (
            <div key={accessType} style={{ marginBottom: 16 }}>
              <SpInput
                label={accessType}
                placeholder="Enter number of slots"
                type="number"
                value={formik.values.accessSlots[accessType]}
                onChange={(e) => handleAccessSlotChange(accessType, e.target.value)}
              />
            </div>
          ))}
        </div>

        {/* Payment Options Section */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '500', 
            color: '#9E9BAC', 
            marginBottom: 16 
          }}>
            Payment Options
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              cursor: 'pointer',
              padding: '12px 0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  backgroundColor: '#F3F4F6', 
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CreditCard size={14} />
                </div>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                  Pay with Bank Transfer
                </span>
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <input
                  type="radio"
                  name="paymentOption"
                  checked={formik.values.paymentOptions.bankTransfer}
                  onChange={(e) => handlePaymentOptionChange('bankTransfer', e.target.checked)}
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    accentColor: '#9747FF'
                  }}
                />
              </div>
            </label>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              cursor: 'pointer',
              padding: '12px 0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  backgroundColor: '#F3F4F6', 
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CreditCard size={14} />
                </div>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                  Pay with Debit Card
                </span>
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <input
                  type="radio"
                  name="paymentOption"
                  checked={formik.values.paymentOptions.card}
                  onChange={(e) => handlePaymentOptionChange('card', e.target.checked)}
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    accentColor: '#9747FF'
                  }}
                />
              </div>
            </label>
          </div>
        </div>
      </form>
    </SwModal>
  );
};

export default SwEventModal;